/**
 * Shared upload configuration for the `documents` domain.
 *
 * One source of truth for every upload surface's max size + allowlist, aligned to the
 * backend allowlist (see `brain/raw/reference/upload-error-handling.md`, BE PR #423).
 * The `accept` attribute, the static "allowed formats" hint, and the client-side
 * pre-validation all derive from the same rule so they can never drift (SPEC AC 5).
 */

const MB = 1024 * 1024;

/** Default per-file limit for document uploads (résumé, CV, attachments). */
export const MAX_UPLOAD_SIZE_BYTES = 5 * MB;

/** Tighter per-file limit for image uploads (profile picture, company logo). */
export const MAX_IMAGE_UPLOAD_SIZE_BYTES = 2 * MB;

/** One allowed file type: primary extension + accepted mimetypes + display label. */
export type UploadAllowedType = {
  /** Leading-dot, lowercase extension, e.g. `.pdf`. The primary pre-validation signal. */
  extension: string;
  /** Mimetypes the backend accepts for this extension (permissive; not the primary check). */
  mimeTypes: readonly string[];
  /** Uppercase display label for hints and error copy, e.g. `PDF`. */
  label: string;
};

/** A per-field upload rule: size cap + allowlist (+ optional multi-file cap). */
export type UploadRule = {
  maxSizeBytes: number;
  allowed: readonly UploadAllowedType[];
  /** Only set for multi-file fields (`additionalDocuments`). */
  maxFiles?: number;
};

const TYPES = {
  png: { extension: ".png", mimeTypes: ["image/png"], label: "PNG" },
  jpeg: { extension: ".jpeg", mimeTypes: ["image/jpeg"], label: "JPEG" },
  jpg: { extension: ".jpg", mimeTypes: ["image/jpeg"], label: "JPG" },
  svg: { extension: ".svg", mimeTypes: ["image/svg+xml"], label: "SVG" },
  pdf: { extension: ".pdf", mimeTypes: ["application/pdf"], label: "PDF" },
  docx: {
    extension: ".docx",
    mimeTypes: [
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
    label: "DOCX",
  },
} as const satisfies Record<string, UploadAllowedType>;

/**
 * Per-field rules. Keys map to the SPEC's upload surfaces:
 * - `profileImage` → `POST /profile/image` (png/jpeg/jpg)
 * - `companyLogo`  → `POST /company/logo/:companyId` (png/jpeg/jpg/svg)
 * - `companyCover` → `POST /company/cover/:companyId` (png/jpeg/jpg)
 * - `resume`       → `POST /profile/resume` (pdf)
 * - `cv`           → `POST /profile/cv-analyzer` (pdf)
 * - `attachments`  → `additionalDocuments` on application/proposal/offer (pdf/docx/png, ×10)
 */
export const UPLOAD_RULES = {
  profileImage: {
    maxSizeBytes: MAX_IMAGE_UPLOAD_SIZE_BYTES,
    allowed: [TYPES.png, TYPES.jpeg, TYPES.jpg],
  },
  companyLogo: {
    maxSizeBytes: MAX_IMAGE_UPLOAD_SIZE_BYTES,
    allowed: [TYPES.png, TYPES.jpeg, TYPES.jpg, TYPES.svg],
  },
  // No SVG, unlike the logo: the cover route runs multer's png/jpeg middleware
  // and re-encodes to webp, so a vector would be refused with a 400.
  companyCover: {
    maxSizeBytes: MAX_IMAGE_UPLOAD_SIZE_BYTES,
    allowed: [TYPES.png, TYPES.jpeg, TYPES.jpg],
  },
  resume: {
    maxSizeBytes: MAX_UPLOAD_SIZE_BYTES,
    allowed: [TYPES.pdf],
  },
  cv: {
    maxSizeBytes: MAX_UPLOAD_SIZE_BYTES,
    allowed: [TYPES.pdf],
  },
  attachments: {
    maxSizeBytes: MAX_UPLOAD_SIZE_BYTES,
    allowed: [TYPES.pdf, TYPES.docx, TYPES.png],
    maxFiles: 10,
  },
} as const satisfies Record<string, UploadRule>;

export type UploadFieldKey = keyof typeof UPLOAD_RULES;
